/**
 * A federated partner's secrets — one row per defined SecretField.
 *
 * The middle store of a three-level chain. An organization's own value
 * wins; this one covers every organization the partner provisioned; the
 * platform value is the last fallback. The status column therefore names
 * three layers where the tenant table names two.
 */
import {
  deletePartnerSecret,
  listPartnerSecrets,
} from '@optum/api-client/endpoints/partner-secrets';
import type { PartnerSecretEntry } from '@optum/api-client/types';
import { ApiError } from '@optum/api-client/client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { CredentialTable, type CredentialRow } from '../../ui/CredentialTable.js';
import { formatSecretTarget } from '../../components/forms/SetSecretValueForm.js';
import { useRecordPanel } from '../../components/shell/record.js';
import { EmptyBlock, ErrorBlock, LoadingBlock } from '../../ui/AsyncState.js';
import { Button } from '../../ui/Button.js';
import { useToast } from '../../ui/useToast.js';
import { qk } from '../../lib/query-keys.js';
import { useStepUp } from '../../lib/step-up.js';

export function PartnerSecretsEditor({ partnerId }: { partnerId: string }) {
  const query = useQuery({
    queryKey: qk.partnerSecrets(partnerId),
    queryFn: () => listPartnerSecrets(partnerId),
  });

  if (query.isPending) return <LoadingBlock />;
  if (query.isError) {
    return (
      <ErrorBlock message={query.error instanceof Error ? query.error.message : 'Failed to load'} />
    );
  }
  const entries = query.data ?? [];
  if (entries.length === 0) {
    return <EmptyBlock>No secret fields defined yet — define them in Security first.</EmptyBlock>;
  }

  const set = entries.filter((e) => e.is_set).length;
  return (
    <CredentialTable
      rows={entries.map<CredentialRow>((entry) => ({
        id: entry.field_key,
        name: entry.field_key,
        sub: entry.label,
        scope: entry.is_secret ? `${entry.type} · secret` : entry.type,
        status: entry.is_set
          ? { label: 'partner', tone: 'blue' }
          : { label: 'platform', tone: 'warn' },
        actions: <SecretActions partnerId={partnerId} entry={entry} />,
      }))}
      count={`${entries.length} secrets · ${set} set for this partner`}
      hint="an organization's own value still wins; an unset key falls back to the platform value"
    />
  );
}

function SecretActions({ partnerId, entry }: { partnerId: string; entry: PartnerSecretEntry }) {
  const panel = useRecordPanel();
  const qc = useQueryClient();
  const toast = useToast();
  // Secret writes demand a fresh password, as on the tenant table.
  const [guardedDelete, deleteDialog] = useStepUp(deletePartnerSecret);


  const clear = useMutation({
    mutationFn: () => guardedDelete(partnerId, entry.field_key),
    onSuccess: () => {
      toast.success(`Cleared ${entry.field_key}`);
      qc.invalidateQueries({ queryKey: qk.partnerSecrets(partnerId) });
    },
    onError: (e) => toast.error('Clear failed', e instanceof ApiError ? e.message : String(e)),
  });

  return (
    <>
      {deleteDialog}
      <Button
        size="sm"
        variant="secondary"
        onClick={() => panel.openForm({ name: 'set-secret', id: formatSecretTarget({ layer: 'partner', partnerId, key: entry.field_key }) })}
      >
        {entry.is_set ? 'Replace' : 'Set value'}
      </Button>
      {entry.is_set && (
        <Button
          size="sm"
          variant="danger"
          onClick={() => clear.mutate()}
          disabled={clear.isPending}
        >
          {clear.isPending ? 'Clearing…' : 'Clear'}
        </Button>
      )}
    </>
  );
}
